import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';
import { applyModuleSeo } from '../../utils/seo';

const CANONICAL = 'https://explanatum.co.uk/modules/link-intelligence';

const LinkIntelligenceLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Link Intelligence Suite | Explanatum';
    const desc = 'Generate branded QR codes, shorten URLs, and track campaign analytics in one workspace—without Bitly fees or agency markups.';
    applyModuleSeo({
      title,
      description: desc,
      canonicalUrl: CANONICAL,
      keywords: [
        'qr code generator',
        'link shortener platform',
        'campaign analytics software',
        'marketing attribution toolkit',
        'explanatum link intelligence'
      ],
      structuredDataId: 'ld-module-link-intelligence',
      softwareName: 'AI Link Intelligence Suite',
      softwareCategory: 'MarketingApplication'
    });
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Link intelligence suite overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 px-4 py-1 text-sm font-semibold text-sky-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-sky-300" />
                AI Link Intelligence Suite
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Build QR codes and short links that prove campaign ROI.
                <span className="mt-2 block text-blue-600">
                  Branded experiences, analytics, automation—in minutes.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Create on-brand QR codes, customizable short links, and real-time dashboards tied to your CRM. Explanatum replaces Bitly subscriptions and agency add-ons with one self-serve growth toolkit.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_link_intelligence_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-blue-600 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/marketing/url-shortener"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open link workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Branded domains & custom QR styling
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Click analytics, UTM rollups, and cohort reporting
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  WhatsApp alerts & CRM sync for milestone events
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Link intelligence dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Campaign summary</span>
                  <span>Live metrics</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">September launch: 57 short links • 12 QR kits • 4.8% conversion rate uplift vs. email-only campaigns.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Top performing link: /expo2025 → 3,189 clicks • 41% mobile • 27% QR scans • WhatsApp alert triggered at 2,000 clicks.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">UTM rollup sync: CRM pipeline updated for 6 opportunities • Consultant drafting follow-up sequences.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Own the link experience from creation to attribution</h2>
            <p className="mt-4 text-base text-gray-500">Run campaigns faster than agencies, keep data in-house, and give stakeholders live visibility.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Short links with governance',
              body: 'Custom domains, reserved codes, click limits, and expiry rules safeguard your brand across teams.'
            }, {
              title: 'Branded QR generation',
              body: 'High-resolution PNG output, color & logo controls, automatic versioning, and download history for compliance.'
            }, {
              title: 'Granular analytics',
              body: 'Referrers, devices, UTM sources, and geography stream into dashboards and can be exported with one click.'
            }, {
              title: 'Automation-ready',
              body: 'Trigger WhatsApp alerts, Slack pings, or CRM tasks when links hit milestones or underperform.'
            }, {
              title: 'Campaign kits & assets',
              body: 'Bundle QR art, short links, and usage instructions into shareable partner or retail kits.'
            }, {
              title: 'Consultant hand-off',
              body: 'The AI Business Consultant reads link performance and spins new creative or follow-up journeys automatically.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams leave Bitly seats and agency bundles</h2>
            <p className="mt-4 text-base text-gray-500">Stop paying per user or waiting on agencies to ship every QR variation. Run experiments instantly while owning the data.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Link Intelligence Suite',
              points: [
                'Unlimited branded domains, QR kits, and team seats included',
                'Campaign analytics export straight into CRM and BI tools',
                'WhatsApp, Slack, and consultant automations when milestones hit',
                'Creative templates and bulk link operations built-in',
                'One price—no add-on upsells for analytics or API access'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Bitly & point solutions',
              points: [
                'Tiered pricing per seat/domain, API access billed separately',
                'Limited QR styling—creative work still handled elsewhere',
                'Analytics live in vendor dashboards with manual CSV exports',
                'Automations require Zapier or custom engineering',
                'Difficult to tie scan/click data back to CRM opportunities'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Marketing agencies with bundled QR/links',
              points: [
                'Setup fees + monthly retainers for basic QR updates',
                'Turnaround measured in days, not minutes',
                'Campaign data delivered as slide decks, not live dashboards',
                'Limited transparency into performance or attribution',
                'No reusable infrastructure—starting from scratch each project'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-indigo-400/30 bg-indigo-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need custom QR kits for a pop-up by Friday?”</h3>
            <p className="mt-3 text-base text-gray-600">Generate the artwork, tracking links, and alerts now—then let the consultant plan follow-up journeys when scans spike.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_link_intelligence_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-blue-600 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-indigo-400/30 hover:scale-[1.02]"
              >
                Launch campaigns now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with the Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where unified link intelligence pays off</h2>
            <p className="mt-4 text-base text-gray-500">Whether you’re activating retail, events, or influencer campaigns, you control the narrative and data.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Retail & OOH activations',
              body: 'Print QR kits with location-based tracking, then alert local teams when scans surge or drop.'
            }, {
              title: 'SaaS growth plays',
              body: 'Spin up short links for product launches, attribution-ready referral programs, and ROI dashboards for leadership.'
            }, {
              title: 'Partner & affiliate kits',
              body: 'Bundle QR art, tracked URLs, and messaging guidelines for partners—monitor click quality across channels.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default LinkIntelligenceLanding;
